import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import TrainerContext from "../../../../context/trainerContext/TrainerContext";
import { FaTimesCircle } from "react-icons/fa";
import { issueTypeGuidelinesMap, chronicIssuesMap } from "./constants";
import makeRequest from "../../../../api";
import {
  assessmentService,
  subscriptionService,
  trainerService,
  userService,
} from "../../../../api/services";
import { toast } from "react-toastify";

const inititalMedicineDetails = {
  name: "",
  timing: "",
  intake: "",
  frequency: "",
  duration: "",
};
const inititalGoalDetails = {
  metricName: "",
  displayName: "",
  currentValue: "",
  targetValue: "",
  measureFrequency: "",
};
const defaultInputData = {
  diagnosis: "",
  goals: [],
  advices: [],
  medicinesDetails: [],
  guidelines: [],
  followup: "",
  issueType: "",
};

const AddPrescription = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const userId = params.userId;
  const issueType = searchParams.get("issueType") || undefined;
  const createdAtEpoch = searchParams.get("createdAt") || undefined;

  const navigate = useNavigate();

  const { getTrainerState } = useContext(TrainerContext);
  const trainerId = getTrainerState()?.trainerId;

  const [inputData, setInputData] = useState<{ [key: string]: any }>(
    defaultInputData
  );
  const [issueTypes, setIssueTypes] = useState<any>([]);
  const [issueTypeOptions, setIssueTypeOptions] = useState<{
    [key: string]: any;
  }>({});
  const [userPlan, setUserPlan] = useState("");
  const [userDetails, setUserDetails] = useState<any>(null);

  const doctorDetailsRef = useRef<any>({
    name: "",
    qualifications: "",
    registrationNumber: "",
    signature: "",
  });
  const userDetailsRef = useRef<any>({
    userId: "",
    name: "",
    age: "",
    gender: "",
  });
  const planNameRef = useRef<any>("");

  const fetchTrainerDetails = async () => {
    try {
      const res = await makeRequest(
        trainerService.getTrainerData(
          {
            trainerId: trainerId,
          },
          trainerId
        )
      );
      const data = res?.data;
      doctorDetailsRef.current = {
        ...doctorDetailsRef.current,
        name: data?.trainer?.name,
        qualifications: data?.trainer?.tags?.[1]?.title,
        registrationNumber: data?.trainer?.tags?.[0]?.title,
        signature: data?.trainer?.signature,
      };
    } catch (error: any) {
      toast.error("Error fetching trainer details!");
    }
  };

  const fetchUserDetails = async () => {
    try {
      const res = await makeRequest(userService.getUserDetails(userId));
      const data = res?.data;
      userDetailsRef.current = {
        ...userDetailsRef.current,
        userId: data?.userDto?.id,
        name: data?.userDto?.fullName,
        age: data?.userDto?.age,
        gender:
          data?.userDto?.gender?.toLowerCase()?.[0]?.toUpperCase() +
          data?.userDto?.gender?.toLowerCase()?.substring(1),
      };
    } catch (error: any) {
      toast.error("Error fetching user details!");
    }
  };

  const fetchCurrentPlanData = async () => {
    try {
      const res = await makeRequest(
        subscriptionService.getUserCurrentActive(userId)
      );
      const plan =
        res?.data?.data?.find((item: any) => item.gabit === "NUTRITION") || {};

      const planName = plan?.healthGoal?.toLowerCase()?.replaceAll("_", " ");
      planNameRef.current =
        planName?.[0]?.toUpperCase() + planName?.substring(1);
      const key = plan?.healthGoal as keyof typeof chronicIssuesMap;
      setUserPlan(chronicIssuesMap[key]);
    } catch (error: any) {
      toast.error("Error fetching user's current active plans!");
    }
  };

  const fetchIssueTypes = async () => {
    try {
      const res = await makeRequest(
        assessmentService.getHealthMetricsOptions()
      );
      setIssueTypes([...res?.data?.body?.issueBasedOptions]);
    } catch (error: any) {
      toast.error("Error fetching health metrics options!");
    }
  };

  const fetchPrescriptions = async () => {
    if (!!issueType && !!createdAtEpoch) {
      try {
        const data = {
          issueType: issueType,
          createdAtEpoch: createdAtEpoch,
        };
        const res = await makeRequest(
          trainerService.getUserPrescriptionDetails(userId, data)
        );
        const response = res?.data?.body;
        setInputData({ ...response });
      } catch (error: any) {
        toast.error("Error fetching prescriptions details!");
      }
    }
  };

  useEffect(() => {
    fetchPrescriptions();
  }, []);

  useEffect(() => {
    fetchTrainerDetails();
    fetchUserDetails();
    fetchCurrentPlanData();
    fetchIssueTypes();
  }, []);

  const getUserData = async () => {
    try {
      const res = await makeRequest(userService.getUserDetails(userId));
      setUserDetails({ ...res?.data?.userDto });
    } catch (error: any) {
      toast.error("Error fetching user details!");
    }
  };

  useEffect(() => {
    if (userId) {
      getUserData();
    }
  }, [userId]);

  useEffect(() => {
    if (issueTypes.length && userPlan !== "") {
      let issue = issueTypes?.find(
        (item: any) => item?.healthIssueType === userPlan
      );
      setIssueTypeOptions({ ...issue?.options });
    }
  }, [issueTypes, userPlan]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "issueType") {
      const str = value as keyof typeof issueTypeGuidelinesMap;
      setInputData({
        ...inputData,
        [name]: value,
        guidelines: issueTypeGuidelinesMap[str],
      });
    } else {
      setInputData({ ...inputData, [name]: value });
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const medicinesDetailsWithEmptyInput = inputData?.medicinesDetails?.some(
      (item: any) =>
        item?.name === "" ||
        item?.timing === "" ||
        item?.intake === "" ||
        item?.frequency === "" ||
        item?.duration === ""
    );
    if (medicinesDetailsWithEmptyInput) {
      alert("Please fill the missing medicine details or remove it.");
      return;
    }
    const advicesWithEmptyInput = inputData?.advices?.some(
      (item: any) => item === ""
    );
    if (advicesWithEmptyInput) {
      alert("Please fill the missing advice input field or remove it.");
      return;
    }
    const goalsWithEmptyInput = inputData?.goals?.some(
      (item: any) =>
        item?.metricName === "" ||
        item?.displayName === "" ||
        item?.currentValue === "" ||
        item?.targetValue === "" ||
        item?.measureFrequency === ""
    );

    if (goalsWithEmptyInput) {
      alert("Please fill the missing goals details or remove it.");
      return;
    }
    if (inputData?.goals?.length === 0) {
      alert("Please enter goals for creating a new prescription!");
      return;
    }
    if (
      inputData?.medicinesDetails?.length === 0 &&
      inputData?.advices?.length === 0
    ) {
      alert(
        "Please enter medicine details or advices for creating a new prescription!"
      );
      return;
    }
    const data = {
      doctorDetails: {
        ...doctorDetailsRef.current,
      },
      userDetails: {
        ...userDetailsRef.current,
      },
      planName: planNameRef.current,
      createdDate: moment().format("DD MMM YYYY, HH:mm a"),
      ...inputData,
    };

    try {
      const res = await makeRequest(
        trainerService.createUserPrescription(data, userId)
      );
      toast.success("Prescription generated successfully!");
      navigate(`/users/${userId}/prescriptions`);
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  const handleGoalAdd = () => {
    setInputData({
      ...inputData,
      goals: [...inputData.goals, inititalGoalDetails],
    });
  };

  const handleMedicineAdd = () => {
    setInputData({
      ...inputData,
      medicinesDetails: [
        ...inputData.medicinesDetails,
        inititalMedicineDetails,
      ],
    });
  };

  const handleAdviceAdd = () => {
    setInputData({
      ...inputData,
      advices: [...inputData.advices, ""],
    });
  };

  const handleListDelete = (name: string, indexToDelete: number) => {
    setInputData({
      ...inputData,
      [name]: inputData?.[name]?.filter?.(
        (item: any, index: number) => index !== indexToDelete
      ),
    });
  };

  const handleListChange = (e: any) => {
    const value = e.target.value;
    const name = e.target.getAttribute("data-name");
    const property = e.target.getAttribute("data-property");
    const indexToEdit = e.target.getAttribute("data-index");

    if (!name?.length) return;

    if (name === "advices") {
      setInputData({
        ...inputData,
        [name]: [
          ...inputData?.[name]?.map((item: any, index: number) => {
            if (index === +indexToEdit) {
              return value;
            } else {
              return item;
            }
          }),
        ],
      });
    } else {
      setInputData({
        ...inputData,
        [name]: [
          ...inputData?.[name]?.map((item: any, index: number) => {
            if (index === +indexToEdit) {
              if (property === "parameter") {
                const [metricName, displayName] = value?.split("#") || [];
                return {
                  ...item,
                  metricName: metricName || "",
                  displayName: displayName,
                };
              } else {
                return {
                  ...item,
                  [property]: value,
                };
              }
            } else {
              return item;
            }
          }),
        ],
      });
    }
  };

  return (
    <div className="container-lg">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb mb-3">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/users/${userId}/reports`}>Reports</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/users/${userId}/prescriptions`}>Prescriptions</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Add Prescriptions
          </li>
        </ol>
      </nav>
      {userDetails?.fullName && (
        <h6 className="fw-700 fs-24 mb-2">
          Add prescription for {userDetails?.fullName}
        </h6>
      )}
      <form onSubmit={handleSubmit}>
        <div className="form-group mb-4 w-50">
          <label htmlFor="issueType" className="form-label">
            <strong>Issue type</strong>
          </label>
          <select
            name="issueType"
            id="issueType"
            className="form-select"
            onChange={handleChange}
            value={inputData?.issueType}
            required
          >
            <option value="">Select issue type</option>
            {issueTypeOptions?.issueLevels?.map((item: any, index: any) => {
              return (
                <option value={item?.displayName} key={index + 1}>
                  {item?.displayName}
                </option>
              );
            })}
          </select>
        </div>
        <hr />
        <div
          className="form-group mb-4"
          style={{
            maxWidth: "50%",
          }}
        >
          <label htmlFor="diagnosis" className="form-label">
            <strong>Diagnosis</strong>
          </label>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Enter diagnosis"
            value={inputData?.diagnosis || ""}
            name="diagnosis"
            id="diagnosis"
            onChange={handleChange}
            required
          />
        </div>
        <hr />
        <div className="form-group mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <label htmlFor="" className="form-label mb-0">
              <strong>Goals</strong>
            </label>
            <button
              type="button"
              className="btn btn-secondary btn-sm px-3"
              onClick={handleGoalAdd}
            >
              Add goal
            </button>
          </div>
          <div>
            {inputData?.goals?.length > 0 && (
              <>
                <div className="d-flex gap-3 mb-3">
                  <div className="d-flex gap-3 w-100">
                    <strong className="w-100">Parameter</strong>
                    <strong className="w-100 ps-2">Current value</strong>
                    <strong className="w-100 ps-2">Target value</strong>
                    <strong className="w-100 ps-2">Measure frequency</strong>
                  </div>
                  <div className="text-white px-3"></div>
                </div>
                {inputData?.goals?.map((item: any, index: number) => (
                  <div
                    key={`goals-details-${index}`}
                    className="d-flex gap-3 mb-3"
                  >
                    <div className="d-flex gap-3 w-100">
                      <select
                        name={`goals-parameter-${index}`}
                        data-name="goals"
                        data-property="parameter"
                        data-index={index}
                        id={`goals-parameter-${index}`}
                        className="form-select"
                        onChange={handleListChange}
                        value={`${item?.metricName}#${item?.displayName}`}
                      >
                        <option value="">Select parameter</option>
                        {issueTypeOptions?.healthMetrics?.map(
                          (item: any, index: any) => {
                            return (
                              <option
                                value={`${item?.value}#${item?.displayName}`}
                                key={index + 1}
                              >
                                {item?.displayName}
                              </option>
                            );
                          }
                        )}
                      </select>
                      <input
                        className="form-control"
                        type="text"
                        name={`goals-currentValue-${index}`}
                        id={`goals-currentValue-${index}`}
                        data-name="goals"
                        data-property="currentValue"
                        data-index={index}
                        onChange={handleListChange}
                        placeholder="Current value (eg: 2-3%)"
                        value={item?.currentValue}
                      />
                      <input
                        className="form-control"
                        type="text"
                        name={`goals-targetValue-${index}`}
                        id={`goals-targetValue-${index}`}
                        data-name="goals"
                        data-property="targetValue"
                        data-index={index}
                        onChange={handleListChange}
                        placeholder="Target value (eg: 7-8%)"
                        value={item?.targetValue}
                      />
                      <input
                        className="form-control"
                        type="text"
                        name={`goals-measureFrequency-${index}`}
                        id={`goals-measureFrequency-${index}`}
                        data-name="goals"
                        data-property="measureFrequency"
                        data-index={index}
                        onChange={handleListChange}
                        placeholder="Measure frequency (eg: Daily- Twice a day)"
                        value={item?.measureFrequency}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm text-danger"
                      onClick={() => {
                        handleListDelete("goals", index);
                      }}
                    >
                      <FaTimesCircle size={20} />
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        <hr />
        <div className="form-group mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <label htmlFor="" className="form-label mb-0">
              <strong>Advices</strong>
            </label>
            <button
              type="button"
              className="btn btn-secondary btn-sm px-3"
              onClick={handleAdviceAdd}
            >
              Add advice
            </button>
          </div>
          <div>
            {inputData?.advices?.map((item: any, index: number) => (
              <div className="d-flex gap-3 mb-3" key={`advices-${index}`}>
                <input
                  className="form-control"
                  type="text"
                  id={`advices-${index}`}
                  name={`advices-${index}`}
                  data-name="advices"
                  data-index={index}
                  onChange={handleListChange}
                  placeholder="Add advice for user"
                  value={item}
                />
                <button
                  type="button"
                  className="btn btn-sm text-danger"
                  onClick={() => {
                    handleListDelete("advices", index);
                  }}
                >
                  <FaTimesCircle size={20} />
                </button>
              </div>
            ))}
          </div>
        </div>
        <hr />
        <div className="form-group mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <label htmlFor="" className="form-label mb-0">
              <strong>Medicines details</strong>
            </label>
            <button
              type="button"
              className="btn btn-secondary btn-sm px-3"
              onClick={handleMedicineAdd}
            >
              Add medicine
            </button>
          </div>
          <div>
            {inputData?.medicinesDetails?.length > 0 && (
              <>
                <div className="d-flex gap-3 mb-3">
                  <div className="d-flex gap-3 w-100">
                    <strong className="w-100">Name</strong>
                    <strong className="w-100 ps-2">Timing</strong>
                    <strong className="w-100 ps-2">Intake</strong>
                    <strong className="w-100 ps-2">Frequency</strong>
                    <strong className="w-100 ps-2">Duration</strong>
                  </div>
                  <div className="text-white px-3"></div>
                </div>
                {inputData?.medicinesDetails?.map(
                  (item: any, index: number) => (
                    <div
                      key={`goals-details-${index}`}
                      className="d-flex gap-3 mb-3"
                    >
                      <div className="d-flex gap-3 w-100">
                        <input
                          className="form-control"
                          type="text"
                          name={`medicinesDetails-name-${index}`}
                          id={`medicinesDetails-name-${index}`}
                          data-name="medicinesDetails"
                          data-property="name"
                          data-index={index}
                          onChange={handleListChange}
                          placeholder="Name (eg: MENOEASE CAP 10'S)"
                          value={item?.name}
                        />
                        <input
                          className="form-control"
                          type="text"
                          name={`medicinesDetails-timing-${index}`}
                          id={`medicinesDetails-timing-${index}`}
                          data-name="medicinesDetails"
                          data-property="timing"
                          data-index={index}
                          onChange={handleListChange}
                          placeholder="Timing (eg: 1 tab - morning)"
                          value={item?.timing}
                        />
                        <input
                          className="form-control"
                          type="text"
                          name={`medicinesDetails-intake-${index}`}
                          id={`medicinesDetails-intake-${index}`}
                          data-name="medicinesDetails"
                          data-property="intake"
                          data-index={index}
                          onChange={handleListChange}
                          placeholder="Intake (eg: 1 tablet)"
                          value={item?.intake}
                        />
                        <input
                          className="form-control"
                          type="text"
                          name={`medicinesDetails-frequency-${index}`}
                          id={`medicinesDetails-frequency-${index}`}
                          data-name="medicinesDetails"
                          data-property="frequency"
                          data-index={index}
                          onChange={handleListChange}
                          placeholder="Frequency (eg: Daily- Twice a day)"
                          value={item?.frequency}
                        />
                        <input
                          className="form-control"
                          type="text"
                          name={`medicinesDetails-duration-${index}`}
                          id={`medicinesDetails-duration-${index}`}
                          data-name="medicinesDetails"
                          data-property="duration"
                          data-index={index}
                          onChange={handleListChange}
                          placeholder="Duration (eg: Till next consultation)"
                          value={item?.duration}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm text-danger"
                        onClick={() => {
                          handleListDelete("medicinesDetails", index);
                        }}
                      >
                        <FaTimesCircle size={20} />
                      </button>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
        <hr />
        <div
          className="form-group mb-4"
          style={{
            maxWidth: "50%",
          }}
        >
          <label htmlFor="followup" className="form-label">
            <strong>Follow ups</strong>
          </label>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Enter follow ups"
            value={inputData?.followup || ""}
            name="followup"
            id="followup"
            onChange={handleChange}
          />
        </div>
        {inputData?.guidelines?.length > 0 && (
          <>
            <hr />
            <div className="form-group mb-4 w-50">
              <label htmlFor="issueType" className="form-label">
                <strong>Guidelines based on issue type</strong>
              </label>
              <ul>
                {inputData?.guidelines?.map((item: any, index: number) => (
                  <li key={`guideline-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
          </>
        )}
        <div className="form-group mb-4">
          <button type="submit" className="btn btn-outline-success px-4">
            Add prescription
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddPrescription;
